import { useEffect, useState } from 'react';
import {
  Form,
  Select,
  AutoComplete,
  Space,
  Spin,
  message,
  Card,
  Switch,
  DatePicker
} from 'antd';
import DocumentService from '../services/Document/DocumentService';
import OrgService from "../services/Org/OrgService";
import ContainerService from '../services/Container/ContainerService';
import dayjs from 'dayjs';
import { useAuth } from '../config/AuthProvider';
import AppButton from '../common/AppButton';
import { useGlobalFilters } from "../providers/GlobalFiltersProvider";
import PurchaseOrderService from "../services/PurchaseOrder/PurchaseOrderService";

const DocumentFilterBarComponent = ({ onSearch, onReset, initialFilters }) => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [advanceFiltersVisible, setAdvanceFiltersVisible] = useState(false);
  const { RangePicker } = DatePicker;
  const { filters } = useGlobalFilters();

  useEffect(() => {
    fetchSuppliers();
    fetchDocumentTypes();
  }, []);

  const fetchSuppliers = () => {
    if (user?.isSupplier) return;
    OrgService.getAllSupplierOrgsDropDown()
      .then((response) => {
        if (response.status === 200) {
          setSuppliers(response.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching supplier:", error);
      });
  };

  const fetchDocumentTypes = () => {
    setLoadingTypes(true);
    DocumentService.getAllDocumentTypesNames()
      .then((response) => {
        if (response.status === 200) {
          setDocumentTypes(response.data);
        }
      })
      .catch((error) => {
        console.error('Error fetching document types:', error);
        message.error('Failed to load document types.');
      })
      .finally(() => {
        setLoadingTypes(false);
      });
  };

  useEffect(() => {
    const setInitialFilters = () => {

      const supplier = suppliers.find(
        (s) => s.id === initialFilters.supplierMaskedID
      );

      const hasAnyValue = Object.values(initialFilters).some(
        (v) => v !== null && v !== undefined && v !== ''
      );

      if (initialFilters) {
        form.setFieldsValue({
          ...initialFilters,
          supplierID: supplier
            ? { value: supplier.id, label: supplier.name }
            : (user?.isSupplier ? { value: user?.supplierID, label: user?.supplierName } : undefined),
          uploadedDate: initialFilters.uploadedDateFrom && initialFilters.uploadedDateTo
            ? [dayjs(initialFilters.uploadedDateFrom), dayjs(initialFilters.uploadedDateTo)]
            : undefined,
          etd: initialFilters.etdFrom && initialFilters.etdTo
            ? [dayjs(initialFilters.etdFrom), dayjs(initialFilters.etdTo)]
            : undefined,
          eta: initialFilters.etaFrom && initialFilters.etaTo
            ? [dayjs(initialFilters.etaFrom), dayjs(initialFilters.etaTo)]
            : undefined,
          paymentDue: initialFilters.paymentDueFrom && initialFilters.paymentDueTo
            ? [dayjs(initialFilters.paymentDueFrom), dayjs(initialFilters.paymentDueTo)]
            : undefined,
        });
      }
      if (initialFilters?.uploadedDateFrom || initialFilters?.uploadedDateTo ||
        initialFilters?.etdFrom || initialFilters?.etdTo ||
        initialFilters?.etaFrom || initialFilters?.etaTo ||
        initialFilters?.paymentDueFrom || initialFilters?.paymentDueTo) {
        setAdvanceFiltersVisible(true);
      }
      if (hasAnyValue) {
        handleSearch();
      }
    };
    setInitialFilters();
  }, [suppliers]);

  const handleSearch = () => {
    form.validateFields().then((values) => {
      delete values.advanceFilters;
      delete values.supplierID;
      let dateFilters = {};
      if (advanceFiltersVisible) {
        dateFilters = transformFilters(values);
      }
      const finalPayload = {
        ...values,
        ...(advanceFiltersVisible ? dateFilters : {}),
      };

      onSearch(finalPayload);
    });
  };

  const handleReset = () => {
    form.resetFields();
    if (onReset) onReset({});
    message.info('Filters and results cleared.');
  };

  const transformFilters = (values) => {
    const makeRange = (range) => ({
      from: range?.[0] ? range[0].startOf('day').toISOString() : null,
      to: range?.[1] ? range[1].endOf('day').toISOString() : null,
    });

    const uploadedDate = makeRange(values.uploadedDate);
    const etd = makeRange(values.etd);
    const eta = makeRange(values.eta);
    const paymentDue = makeRange(values.paymentDue);

    return {
      uploadedDateFrom: uploadedDate.from,
      uploadedDateTo: uploadedDate.to,
      etdFrom: etd.from,
      etdTo: etd.to,
      etaFrom: eta.from,
      etaTo: eta.to,
      paymentDueFrom: paymentDue.from,
      paymentDueTo: paymentDue.to,
    };
  };

  const [containerNumSearchInput, setContainerNumSearchInput] = useState('');
  const [poNumberSearchInput, setPoNumberSearchInput] = useState('');

  const [containerNumSearchResults, setContainerNumSearchResults] = useState([]);
  const [poNumberSearchResults, setPoNumberSearchResults] = useState([]);

  const [containerNumSearchResultsloading, setContainerNumSearchResultsLoading] = useState(false);
  const [poNumberSearchResultsloading, setPoNumberSearchResultsLoading] = useState(false);

  const handleContainerNumChange = (value) => {
    if (value) {
      form.setFieldsValue({ containerNum: value });
      setContainerNumSearchInput(value);
    }
  }

  const handlePoNumberChange = (value) => {
    if (value) {
      form.setFieldsValue({ poNumber: value });
      setPoNumberSearchInput(value);
    }
  }

  useEffect(() => {
    if (!containerNumSearchInput) {
      setContainerNumSearchResults([]);
      return;
    }

    const delayDebounce = setTimeout(() => {
      setContainerNumSearchResultsLoading(true);
      ContainerService.searchContainer(containerNumSearchInput)
        .then((response) => {
          setContainerNumSearchResults(response);
        }).catch((error) => {
          setContainerNumSearchResults([]);
          console.error('Error searching containers:', error);
        }).finally(() => {
          setContainerNumSearchResultsLoading(false);
        });
    }, 1000);

    return () => clearTimeout(delayDebounce);
  }, [containerNumSearchInput]);

  useEffect(() => {
    if (!poNumberSearchInput) {
      setPoNumberSearchResults([]);
      return;
    }

    const delayDebounce = setTimeout(() => {
      setPoNumberSearchResultsLoading(true);
      PurchaseOrderService.searchPurchaseOrdersWithFilters({ purchaseOrderNumber: poNumberSearchInput, ...filters })
        .then((response) => {
          setPoNumberSearchResults(response);
        }).catch((error) => {
          setPoNumberSearchResults([]);
          console.error('Error searching PO numbers:', error);
        }).finally(() => {
          setPoNumberSearchResultsLoading(false);
        });
    }, 1000);

    return () => clearTimeout(delayDebounce);
  }, [poNumberSearchInput]);

  return (<div className="mx-4">
    <Form form={form} layout="vertical" className=''>
      <Card className="pb-1 rounded-3xl bg-white bg-opacity-40 ">
        <div className='grid grid-cols-7 gap-x-5 gap-y-2'>
          <Form.Item name="supplierID" label="Supplier">
            <Select
              disabled={user?.isSupplier}
              showSearch
              className="h-[34px]"
              optionFilterProp="label"
              labelInValue
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
              onChange={(selected) => {
                if (!selected) {
                  form.setFieldsValue({
                    supplierID: undefined,
                    supplierMaskedID: null,
                  });
                  return;
                }
                const supplier = suppliers.find(
                  (c) => c.id === selected.value
                );
                form.setFieldsValue({
                  supplierID: {
                    value: supplier.id,
                    label: supplier.name,
                  },
                  supplierMaskedID: supplier.id,
                });
              }}
              allowClear
              placeholder="Select Supplier"
              onClear={() => {
                form.setFieldValue({
                  supplierID: undefined,
                  supplierMaskedID: null,
                })
              }}
              options={suppliers.map((c) => (
                { value: c.id, label: c.name }
              ))
              }
            />
          </Form.Item>
          <Form.Item name="supplierMaskedID" hidden>
            <input className='!h-[34px]' type="hidden" />
          </Form.Item>
          <Form.Item name="blNum" label="BL">
            <AutoComplete
              className='!h-[34px]'
              placeholder="BL Number"
              value={form.getFieldValue('blNum')}
            // onChange={(value) => {
            //   form.setFieldsValue({ blNum: value });
            //   // handleFieldChange('blNum', value);
            // }}
            />
          </Form.Item>
          <Form.Item name="containerNum" label="Container Num">
            <AutoComplete
              className='!h-[34px]'
              placeholder="Container Number"
              value={form.getFieldValue('containerNum')}
              options={containerNumSearchResults.map(c => ({ value: c.containerNum }))}
              onChange={handleContainerNumChange}
              onSelect={(value) => form.setFieldsValue({ containerNum: value })}
              allowClear
              notFoundContent={
                containerNumSearchResultsloading ? <Spin size="small" /> : "No results found"
              }
            />
          </Form.Item>
          <Form.Item name="poNumber" label="PO Number">
            <AutoComplete
              className='!h-[34px]'
              placeholder="PO Number"
              value={form.getFieldValue('poNumber')}
              options={poNumberSearchResults.map(c => ({ value: c.purchaseOrderNumber }))}
              onChange={handlePoNumberChange}
              onSelect={(value) => form.setFieldsValue({ poNumber: value })}
              allowClear
              notFoundContent={
                poNumberSearchResultsloading ? <Spin size="small" /> : "No results found"
              }
            />
          </Form.Item>
          <Form.Item name="userReference" label="User Reference">
            <AutoComplete
              className='!h-[34px]'
              placeholder="User Reference"
              value={form.getFieldValue('userReference')}
            // onChange={(value) => {
            //   form.setFieldsValue({ userReference: value });
            //   // handleFieldChange('userReference', value);
            // }}
            />
          </Form.Item>
          <Form.Item name="documentType" label="Document Type" className='col-span-2'>
            <Select
              className='!h-[34px]'
              mode="multiple"
              placeholder="Select one or more"
              loading={loadingTypes}
              notFoundContent={loadingTypes ? <Spin size="small" /> : null}
              // onChange={(value) => handleFieldChange('documentType', value)}
              allowClear
              maxTagCount={1}
              options={documentTypes.map((type) => (
                { value: type, label: type }
              ))}
            />
          </Form.Item>
          <Form.Item name="isStockReceived" label="Stock Received" layout='horizontal' colon={false} className='col-span-2'>
            <Switch
              onChange={(e) => {
                form.setFieldValue('isStockReceived', e)
                if (e === false) {
                  form.setFieldValue('isStockReceived', null);
                }
              }}
            >
              Stock Received
            </Switch>
          </Form.Item>
          <Form.Item name="isPaid" label="Paid" layout='horizontal' colon={false}>
            <Switch
              onChange={(e) => {
                form.setFieldValue('isPaid', e)
                if (e === false) {
                  form.setFieldValue('isPaid', null);
                }
              }}
            >
              Paid
            </Switch>
          </Form.Item>
          <Form.Item name="advanceFilters" label="Advance Filters" layout='horizontal' colon={false}>
            <Switch checked={advanceFiltersVisible} onChange={(checked) => {
              setAdvanceFiltersVisible(checked);
              if (!checked) {
                // clear advanced fields so they don't leak into payload
                form.setFieldsValue({
                  uploadedDate: null,
                  etd: null,
                  eta: null,
                  paymentDue: null,
                });
              }
            }}>
              Advanced Filters
            </Switch>
          </Form.Item>
        </div>
        {advanceFiltersVisible && (
          <Card className="pb-1 rounded-3xl bg-white bg-opacity-40 w-full">
            <div className='grid grid-cols-4 gap-x-5 gap-y-3'>
              <Form.Item layout='vertical' name="uploadedDate" label="Uploaded Date Range">
                <RangePicker className='!h-[34px]' />
              </Form.Item>

              <Form.Item name="etd" layout='vertical' label="ETD Range">
                <RangePicker className='!h-[34px]' />
              </Form.Item>

              <Form.Item name="eta" layout='vertical' label="ETA Range">
                <RangePicker className='!h-[34px]' />
              </Form.Item>

              <Form.Item name="paymentDue" layout='vertical' label="Payment Due Date Range">
                <RangePicker className='!h-[34px]' />
              </Form.Item>
            </div>
          </Card>
        )}
      </Card>
      <div className='flex justify-end mt-4 mb-5 mr-2'>
        <Space wrap>
          <AppButton variant='search' onClick={handleSearch}>
            Search
          </AppButton>
          <AppButton variant='clearFilter' onClick={handleReset}>
            Clear Filter
          </AppButton>
        </Space>
      </div>
    </Form>
  </div>
  );
};

export default DocumentFilterBarComponent;
